import { array } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { add, get, isEmpty, isEqual, map, negate, nth, reduce, subtract, sum, uniqWith } from 'lodash/fp';
import { createSelector, Selector } from 'reselect';

import { TimeUnit } from '@mews/enums';
import { getNumberProp, getOrEmptyArray, getOrZero, getStringProp, values } from '@mews/fp';
import { DateTimeUtcService, DateTimeUtils, TimeUnitUtils, WallClockDateService } from '@mews/framework/datetime';
import { createPropsExtender } from '@mews/framework/redux';

import { parentSelector } from '../../selectors/parentSelector';
import { ApplicationState } from '../../types';
import { isAgeCategoryAdultClassification } from '../entities/ageCategories/selectors';
import { getEnterpriseTimezone } from '../entities/enterprises/selectors';
import { getServiceTimeUnit, isServiceDayUse, isServiceHourly } from '../entities/services/selectors';
import { hasOccupanciesNonEmptyOccupancy } from './occupancyUtils';
import { ResourceOrder } from './types';

export const getResourceOrder = createSelector<ApplicationState, ApplicationState, ResourceOrder>(
    parentSelector,
    get('resourceOrder')
);

/** If possible, avoid using this contextual selector anywhere but in the uppermost components. We should instead prefer context-less selectors and passing enterpriseId as a prop. It makes code more predictable, testable and components more reusable. */
export const getSelectedHotelId = flow<[ApplicationState], ResourceOrder, string>(
    getResourceOrder,
    get<ResourceOrder, 'hotelId'>('hotelId')
);

/**
 * If possible, avoid using this contextual extender in new code. We should instead prefer context-less selectors. It makes code more predictable, testable and components more reusable.
 * For example instead of using wrapper selector `getSelectedEnterpriseTimezone`, prefer directly using `getEnterpriseTimezone`.
 */
export const selectedEnterpriseIdPropsExtender = createPropsExtender('enterpriseId', getSelectedHotelId);

export const getStartDate = flow(getResourceOrder, get<ResourceOrder, 'startDate'>('startDate'));
export const getEndDate = flow(getResourceOrder, get<ResourceOrder, 'endDate'>('endDate'));
export const getStartTimeUtc = flow(getResourceOrder, get<ResourceOrder, 'startTimeUtc'>('startTimeUtc'));
export const getEndTimeUtc = flow(getResourceOrder, get<ResourceOrder, 'endTimeUtc'>('endTimeUtc'));
export const getRoomId = flow(getResourceOrder, get<ResourceOrder, 'roomId'>('roomId'));
export const getProductIds = flow(getResourceOrder, getOrEmptyArray('productIds'));
export const getSelectedPromotedServices = flow(getResourceOrder, (resourceOrder) => resourceOrder.promotedServices);

export const getVoucherCode = flow(getResourceOrder, get<ResourceOrder, 'voucherCode'>('voucherCode'));
export const isFlexiblePricesEnabled = flow(getResourceOrder, (resourceOrder) => resourceOrder.showFlexiblePrices);
export const hasVoucherCode = flow(getVoucherCode, negate(isEmpty));
export const isVoucherDirty = flow(getResourceOrder, get<ResourceOrder, 'isVoucherDirty'>('isVoucherDirty'), Boolean);

export const getOccupancies = flow(getResourceOrder, getOrEmptyArray('occupancies'));

export const areOccupanciesNonEmpty = createSelector(getOccupancies, hasOccupanciesNonEmptyOccupancy);

const getOccupancy = createSelector(getNumberProp('categoryIndex'), getOccupancies, (categoryIndex, occupancies) =>
    nth(categoryIndex, occupancies)
);

export const getStartTime = createSelector(getStartTimeUtc, getEnterpriseTimezone, (startTimeUtc, timezone) => {
    if (startTimeUtc == null || timezone == null) {
        return undefined;
    }
    return DateTimeUtils.dateTimeUtcStringToTime(startTimeUtc, timezone);
});
export const getEndTime = createSelector(getEndTimeUtc, getEnterpriseTimezone, (endTimeUtc, timezone) => {
    if (endTimeUtc == null || timezone == null) {
        return undefined;
    }
    return DateTimeUtils.dateTimeUtcStringToTime(endTimeUtc, timezone);
});

const getOccupancyData = flow(getOccupancy, get('occupancyData'));
const getAgeCategoryOccupancyData = createSelector(
    getStringProp('ageCategoryId'),
    getOccupancyData,
    (ageCategoryId, occupancyData) => get(ageCategoryId, occupancyData)
);

export const getAgeCategoryOccupancyDataPersonCount = flow(getAgeCategoryOccupancyData, getOrZero('personCount'));

interface WithCategoryIndex {
    categoryIndex: number;
}

export const getAgeCategoryOccupancyDataAdultChildCounts = (
    state: ApplicationState,
    { categoryIndex }: WithCategoryIndex
) => {
    const occupancy = getOccupancyData(state, { categoryIndex });

    return reduce(
        ({ adultCount, childCount }, { ageCategoryId, personCount }) =>
            isAgeCategoryAdultClassification(state, { ageCategoryId })
                ? { adultCount: personCount + adultCount, childCount }
                : { childCount: personCount + childCount, adultCount },
        { adultCount: 0, childCount: 0 },
        occupancy
    );
};

const getOccupancyDataPersonCounts = createSelector(getOccupancyData, map('personCount'));
const getOccupiedPersonCount = createSelector(getOccupancyDataPersonCounts, sum);

const getRemainingPersonCount = createSelector(getNumberProp('maxPersonCount'), getOccupiedPersonCount, subtract);

export const getOccupanciesTotalPersonCount = createSelector(getOccupancies, (occupancies) =>
    occupancies.reduce((totalPersonCount, occupancy) => {
        const occupancyCounts = values(occupancy.occupancyData);
        const occupancyTotal = occupancyCounts.reduce((count, { personCount }) => count + personCount, 0);
        return totalPersonCount + occupancyTotal;
    }, 0)
);

export const getResourceOrderOccupancyCount = createSelector(getOccupancies, (occupancies) => occupancies.length);
export const getMaxAgeCategoryCount = createSelector(
    getRemainingPersonCount,
    getAgeCategoryOccupancyDataPersonCount,
    add
);

export const isHotelSelected = createSelector(getSelectedHotelId, (hotelId) => !!hotelId);

export const getCategoryExtraBedCount = createSelector(
    getOccupiedPersonCount,
    getNumberProp('normalBedCount'),
    (personCount, normalBedCount) => Math.max(0, personCount - normalBedCount)
);

export const getSpaceTypeCount = flow(getOccupancies, array.size);

export const getUniqueOccupancies = createSelector(getOccupancies, (occupancies) => uniqWith(isEqual, occupancies));

export const areDatesSelected = createSelector(
    getStartDate,
    getEndDate,
    (startDate, endDate) => startDate != null && endDate != null
);

const createParsedDateSelector = (dateSelector: Selector<ApplicationState, string | undefined>) =>
    createSelector(dateSelector, (date) => (date != null ? WallClockDateService.parse(date) : null));
export const getParsedStartDate = createParsedDateSelector(getStartDate);
export const getParsedEndDate = createParsedDateSelector(getEndDate);

export const getUniqueOccupanciesOccupancyData = createSelector(
    getUniqueOccupancies,
    array.flatMap(({ occupancyData }) => values(occupancyData))
);

export const getSelectedResourceOrderTimeUnitCount = createSelector(
    getStartDate,
    getEndDate,
    getServiceTimeUnit,
    isServiceDayUse,
    getStartTimeUtc,
    getEndTimeUtc,
    isServiceHourly,
    (startDate, endDate, timeUnit, isDayService, startTime, endTime, isHourlyService) => {
        if (startDate == null || endDate == null) {
            return 0;
        }
        const startDateTime = WallClockDateService.parse(startDate);
        const endDateTime = WallClockDateService.parse(endDate);
        const dateTime = TimeUnitUtils.timeUnitToDateTimePart(timeUnit);
        const withExtraCount = (timeUnit === TimeUnit.Day && isDayService) || timeUnit === TimeUnit.Month;

        if (isHourlyService && startTime != null && endTime != null) {
            const start = DateTimeUtcService.parse(startTime);
            const end = DateTimeUtcService.parse(endTime);
            const diff = DateTimeUtcService.diffHours(end, start);
            return diff <= 0 ? 1 : diff;
        }

        if (withExtraCount) {
            const end = WallClockDateService.startOf(dateTime)(endDateTime);
            const start = WallClockDateService.startOf(dateTime)(startDateTime);

            return WallClockDateService.diff(dateTime)(end, start) + 1;
        }

        return WallClockDateService.diff(dateTime)(endDateTime, startDateTime);
    }
);
